import React, { useEffect, useMemo, useState } from 'react';
import { canCurrentUser } from '@utils/Permissions';
import { useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { Capability } from '@utils/Permissions/Capabilities';
import {
    Box,
    Card,
    Grid,
    Stack,
    Tab,
    Tabs,
    Typography,
    IconButton,
    Drawer,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import { Replay, TrendingUp, ReceiptLong, FilterList } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { currencyFormatter } from '@utils/Numbers';
import OrdersService from '@api/Orders/OrderCtrl';
import UserService from '@api/Users/UserCtrl';
import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';
import { ProfileTypes, UserType } from '@api/Users/User';
import DashboardAnalytics from '@screens/Dashboard/components/DashboardAnalytics';
import OrdersTable from '@screens/Dashboard/components/OrdersTable';
import FilterSidebar from '@screens/Dashboard/components/FilterSidebar';
import CashRegistersTab from '@screens/Dashboard/components/CashRegistersTab';

const today = startOfDay(new Date());
const endToday = endOfDay(today);

/** Tabs de primer nivel. Tabla vs analíticas ya NO es un tab: es un switch dentro de la card */
type DashboardTab = 'orders' | 'cashRegisters';
type OrdersView = 'table' | 'analytics';

const DashboardScreen: React.FunctionComponent = () => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    // Las sucursales de los filtros y de Cajas: las que ve quien opera (en "Ver como", la del sidebar).
    const branchOffices = useVisibleBranchOffices();
    const ordersService = useMemo(() => new OrdersService(), []);
    const usersService = useMemo(() => new UserService(), []);

    const [orders, setOrders] = useState<Order[]>([]);
    const [collaborators, setCollaborators] = useState<UserType[]>([]);
    // El tab inicial puede venir en la URL: la notificación de "caja sin cerrar"
    // abre `?tab=cashRegisters` para dejar al usuario donde se resuelve, en vez
    // de en Órdenes obligándolo a buscar (pedido de Irving, 2026-08-29).
    const [currentTab, setCurrentTab] = useState<DashboardTab>(() => {
        const pedido = new URLSearchParams(window.location.search).get('tab');
        return pedido === 'cashRegisters' ? 'cashRegisters' : 'orders';
    });
    const [ordersView, setOrdersView] = useState<OrdersView>('table');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const [filters, setFilters] = useState({
        startDate: today,
        endDate: endToday,
        status: 'all',
        branchOfficeId: branchOfficeSelected?.id || '',
        deliveryType: 'all',
        hasUber: 'all',
        collaboratorId: 'all'
    });

    const [tempFilters, setTempFilters] = useState(filters);

    const getInitialData = async () => {
        showLoader();
        try {
            const collabs = await usersService.getUsersByProfile(ProfileTypes.Collaborator);
            setCollaborators(collabs);
            await loadTabData();
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const loadTabData = async () => {
        showLoader();
        try {
            await getOrdersData();
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const getOrdersData = async () => {
        const ordersData = await ordersService.getOrders(
            filters.status !== 'all' ? (filters.status as OrderStatus) : undefined,
            filters.startDate,
            filters.endDate,
            filters.branchOfficeId !== 'all' ? filters.branchOfficeId : undefined,
            undefined,
            filters.deliveryType !== 'all' ? (filters.deliveryType as DeliveryType) : undefined
        );

        const filteredOrders = ordersData.filter((order) => {
            if (filters.hasUber === 'yes') return !!order.uberDeliveryId;
            if (filters.hasUber === 'no') return !order.uberDeliveryId;
            return !(
                filters.collaboratorId &&
                filters.collaboratorId !== 'all' &&
                order.collaboratorId !== filters.collaboratorId
            );
        });

        setOrders(filteredOrders);
    };

    useEffect(() => {
        void getInitialData();
    }, []);

    useEffect(() => {
        void loadTabData();
    }, [filters]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: DashboardTab) => {
        setCurrentTab(newValue);
    };

    const handleOpenFilterDrawer = () => {
        setTempFilters(filters);
        setFilterDrawerOpen(true);
    };

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false);
    };

    const handleApplyFilters = () => {
        setFilters(tempFilters);
        setFilterDrawerOpen(false);
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            startDate: today,
            endDate: endToday,
            status: 'all',
            branchOfficeId:
                branchOfficeSelected?.id ||
                (canCurrentUser(Capability.IncomeStatementConsolidated) ? 'all' : branchOffices[0]?.id || ''),
            deliveryType: 'all',
            hasUber: 'all',
            collaboratorId: 'all'
        };
        setTempFilters(clearedFilters);
        setFilters(clearedFilters);
        setFilterDrawerOpen(false);
    };

    return (
        // boxSizing explícito: CssBaseline solo se monta en las pantallas de login,
        // así que aquí el padding se SUMA al height 100% y la pantalla termina 32px
        // más alta, con scroll de página (la regla del proyecto es scroll interno).
        <Stack height="100%" p={2} spacing={2} boxSizing="border-box">
            <Drawer
                anchor="left"
                open={filterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 350,
                        p: 3,
                        boxSizing: 'border-box'
                    }
                }}
            >
                <FilterSidebar
                    filters={tempFilters}
                    onFilterChange={setTempFilters}
                    branchOffices={branchOffices}
                    collaborators={collaborators}
                    // El admin de sucursal ve el Dashboard de las suyas, una a la vez (F6).
                    forbidAllBranches={!canCurrentUser(Capability.IncomeStatementConsolidated)}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                />
            </Drawer>

            {/* Tabs de primer nivel. Los filtros de órdenes solo aplican a su tab:
                Cajas trae su propio filtro de día y su recarga. */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Tabs value={currentTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
                    <Tab label="Órdenes" value="orders" sx={{ fontWeight: 600, fontSize: 15 }} />
                    <Tab label="Cajas" value="cashRegisters" sx={{ fontWeight: 600, fontSize: 15 }} />
                </Tabs>
                {currentTab === 'orders' && (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            color="primary"
                            onClick={handleOpenFilterDrawer}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                            <FilterList />
                        </IconButton>
                        <IconButton
                            color="secondary"
                            onClick={() => loadTabData()}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                            <Replay />
                        </IconButton>
                    </Stack>
                )}
            </Stack>

            {/* Card principal a todo el alto; el scroll vive DENTRO */}
            <Stack component={Card} flex={1} minHeight={0} overflow="hidden" boxSizing="border-box">
                {currentTab === 'orders' && (
                    <Stack height="100%" minHeight={0} spacing={3}>
                        {/* KPIs del filtro + switch tabla/analíticas DENTRO de la card */}
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                                                Venta Total
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700}>
                                                {currencyFormatter(orders.reduce((sum, order) => sum + order.total, 0))}
                                            </Typography>
                                        </Box>
                                        <TrendingUp sx={{ fontSize: 40, opacity: 0.5 }} />
                                    </Stack>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Total de Órdenes
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} color="text.primary">
                                                {orders.length}
                                            </Typography>
                                        </Box>
                                        <ReceiptLong sx={{ fontSize: 40, color: 'text.disabled' }} />
                                    </Stack>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                    <ToggleButtonGroup
                                        color="primary"
                                        size="small"
                                        exclusive
                                        value={ordersView}
                                        onChange={(event, value: OrdersView | null) => value && setOrdersView(value)}
                                    >
                                        <ToggleButton value="table" sx={{ px: 2 }}>
                                            Tabla
                                        </ToggleButton>
                                        <ToggleButton value="analytics" sx={{ px: 2 }}>
                                            Analíticas
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                            </Grid>
                        </Grid>

                        {ordersView === 'table' ? (
                            <Box flex={1} minHeight={0}>
                                <OrdersTable
                                    orders={orders}
                                    branchOffices={branchOffices}
                                    getOrdersData={() => getOrdersData()}
                                />
                            </Box>
                        ) : (
                            <Box flex={1} minHeight={0} sx={{ overflowY: 'auto' }}>
                                <DashboardAnalytics orders={orders} />
                            </Box>
                        )}
                    </Stack>
                )}

                {currentTab === 'cashRegisters' && <CashRegistersTab branchOffices={branchOffices} />}
            </Stack>
        </Stack>
    );
};

export default DashboardScreen;
